import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Loader2, Star, MessageSquareQuote, Plus, Pencil, Save
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import RecordActions from '../../components/RecordActions';
import { AdminSubNav, SUBNAV_CONTENT } from '../../components/admin/AdminSubNav';
import ReviewFollowUpDialog, { FOLLOW_UP } from '../../components/admin/ReviewFollowUpDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RATINGS = [1, 2, 3, 4, 5];
const emptyForm = { name: '', rating: 5, service: '', text: '', date: '', display_order: 0, is_published: true };

const AdminReviewsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/settings');
  const [reviews, setReviews] = useState([]);
  const [fuFilter, setFuFilter] = useState('All');
  const [followUp, setFollowUp] = useState(null);
  const shown = reviews.filter((r) => fuFilter === 'All' || (r.follow_up_status || 'New') === fuFilter);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/reviews`, { headers });
      setReviews(res.data);
    } catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || '',
      rating: r.rating || 5,
      service: r.service || '',
      text: r.text || '',
      date: r.date || '',
      display_order: r.display_order ?? 0,
      is_published: r.is_published !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast.error('Name and review text are required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, rating: parseInt(form.rating, 10), display_order: parseInt(form.display_order, 10) || 0 };
      if (editing) {
        await axios.put(`${API_URL}/api/admin/reviews/${editing.id}`, payload, { headers });
        toast.success('Review updated');
      } else {
        await axios.post(`${API_URL}/api/admin/reviews`, payload, { headers });
        toast.success('Review added');
      }
      setDialogOpen(false);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const togglePublish = async (r) => {
    try {
      await axios.post(`${API_URL}/api/admin/reviews/${r.id}/toggle-publish`, {}, { headers });
      toast.success(r.is_published === false ? 'Review published' : 'Review unpublished');
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle');
    }
  };

  const publishedCount = reviews.filter(r => r.is_published !== false).length;
  const draftCount = reviews.length - publishedCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-reviews-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="text-sm text-[#77776e]">Private follow-up on client feedback. Only testimonials switched “On website” appear on the homepage.</p>
          <Button onClick={openCreate} className="bg-[#f36a19] hover:bg-[#C2410C] text-[#171912] font-bold flex-shrink-0" data-testid="add-review-btn">
            <Plus className="h-4 w-4 mr-2" /> Log feedback
          </Button>
        </div>

        <AdminSubNav section="Content" items={SUBNAV_CONTENT} />

        <div className="flex flex-wrap gap-3 mb-6 mt-4">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 rounded-full text-xs px-3 py-1" data-testid="published-count">
            {publishedCount} live on site
          </Badge>
          {draftCount > 0 && (
            <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-200 rounded-full text-xs px-3 py-1" data-testid="draft-count">
              {draftCount} draft{draftCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <label className="text-xs text-[#5f6455] flex items-center gap-2">STATUS
            <select value={fuFilter} onChange={(e) => setFuFilter(e.target.value)} className="h-9 rounded-md border border-[#dcded2] bg-white px-2 text-sm" data-testid="review-status-filter">
              {['All', ...FOLLOW_UP].map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        {shown.length === 0 ? (
          <div className="rounded-[10px] border border-[#e7e4db] bg-[#fffefa] py-16 text-center" data-testid="reviews-empty">
            <MessageSquareQuote className="h-12 w-12 mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-600 font-medium mb-1">{reviews.length ? 'No feedback in this view.' : 'No reviews yet'}</p>
            <p className="text-zinc-400 text-sm mb-6">{reviews.length ? 'Change the status filter to see other records.' : 'Log the first piece of client feedback.'}</p>
            {!reviews.length && <Button onClick={openCreate} className="bg-[#F56F05] hover:bg-[#C2410C] text-white" data-testid="empty-add-review-btn"><Plus className="h-4 w-4 mr-2" /> Log feedback</Button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="reviews-grid">
            {shown.map((r, i) => (
              <motion.article key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className={`rounded-[10px] border border-[#e7e4db] bg-[#fffefa] p-5 flex flex-col ${r.is_published === false ? 'opacity-80' : ''}`} data-testid={`review-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[#f36a19] tracking-[0.15em]" aria-label={`${r.rating} out of 5`}>{'★'.repeat(r.rating || 0)}<span className="text-[#d8d5c9]">{'★'.repeat(5 - (r.rating || 0))}</span></span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${(r.follow_up_status || 'New') === 'Reviewed' ? 'bg-[#eef3e8] text-[#55704a]' : (r.follow_up_status || 'New') === 'Needs follow-up' ? 'bg-[#fde3d3] text-[#a4471b]' : 'bg-[#efeee6] text-[#5f6455]'}`} data-testid={`review-status-${r.id}`}>{r.follow_up_status || 'New'}</span>
                </div>
                <blockquote className="text-sm text-[#3f4038] leading-relaxed mt-3 flex-1">“{r.text}”</blockquote>
                <div className="mt-4"><strong className="block text-sm text-[#20211d]">{r.name}</strong><small className="text-[11px] text-[#77776e]">{r.service ? `${r.service} · ` : ''}{r.date}{r.owner_name ? ` · Owner: ${r.owner_name}` : ''}</small></div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#ecebe4]">
                  <button onClick={() => setFollowUp(r)} className="text-xs font-semibold text-[#b75b28]" data-testid={`review-followup-${r.id}`}>Review & follow up ↗</button>
                  <span className="flex-1" />
                  <span className="text-[10px] text-[#77776e]">{r.is_published === false ? 'Draft' : 'On website'}</span>
                  <Switch checked={r.is_published !== false} onCheckedChange={() => togglePublish(r)} data-testid={`toggle-publish-${r.id}`} />
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-review-${r.id}`}><Pencil className="h-4 w-4 text-zinc-400" /></Button>
                  <RecordActions canEdit={false} canArchive={false} canDelete={true} deleteUrl={`/api/admin/reviews/${r.id}`} deleteConfirmTitle={`Delete review from ${r.name}?`}
                    deleteConfirmDescription="Permanently removes this review from your website and database. This cannot be undone." onDeleted={fetchReviews} headers={headers} testIdPrefix="review" entityId={r.id} />
                </div>
              </motion.article>
            ))}
          </div>
        )}
        <p className="text-xs text-[#77776e] mt-5">Owner, status and response drafts are private. Only the testimonial text marked “On website” appears on the homepage.</p>
        <ReviewFollowUpDialog review={followUp} onClose={() => setFollowUp(null)} headers={headers} onSaved={() => { setFollowUp(null); fetchReviews(); }} />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquareQuote className="h-4 w-4 text-[#F56F05]" />
              {editing ? 'Edit Review' : 'Add Review'}
            </DialogTitle>
            <DialogDescription>
              {editing ? 'Update this customer testimonial.' : 'Add a new customer testimonial to display on the homepage.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sarah M." data-testid="review-name" />
              </div>
              <div>
                <Label className="text-xs">Rating *</Label>
                <div className="flex gap-1 mt-1.5">
                  {RATINGS.map(n => (
                    <button
                      type="button"
                      key={n}
                      onClick={() => setForm({ ...form, rating: n })}
                      className="p-0.5"
                      data-testid={`rating-${n}`}
                    >
                      <Star className={`w-6 h-6 transition-colors ${n <= form.rating ? 'text-[#F56F05] fill-[#F56F05]' : 'text-zinc-200'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Service (optional)</Label>
                <Input value={form.service} onChange={e => setForm({ ...form, service: e.target.value })} placeholder="e.g. Signature Deep Reset" data-testid="review-service" />
              </div>
              <div>
                <Label className="text-xs">Date label (optional)</Label>
                <Input value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} placeholder="e.g. January 2026" data-testid="review-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Review text *</Label>
              <Textarea
                value={form.text}
                onChange={e => setForm({ ...form, text: e.target.value })}
                rows={4}
                placeholder="What did the customer say about their experience?"
                data-testid="review-text"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">Display order (0 = top)</Label>
                <Input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })} data-testid="review-order" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="review-publish-switch" />
                <Label className="text-xs cursor-pointer" onClick={() => setForm({ ...form, is_published: !form.is_published })}>
                  {form.is_published ? 'Live on homepage' : 'Draft (hidden)'}
                </Label>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={save} disabled={saving} data-testid="review-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editing ? 'Save changes' : 'Add review'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReviewsPage;
